import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, MouseSensor, TouchSensor, useSensor, useSensors, useDraggable, DragOverlay, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getToken, hasPermission } from '../services/authService';
import { getAllPagesAdmin, addPage, updatePage, deletePage, uploadPageImage } from '../services/pageService';
import { getBrandLogos, setBrandLogoUrl, uploadBrandLogo, deleteBrandLogo } from '../services/brandLogoService';
import API_BASE_URL from '../config';
import { getCategories } from '../services/categoryService';
import { getBlogCategories } from '../services/blogCategoryService';
import './Admin.css';
import toast from 'react-hot-toast';
import ModuleEditor from '../components/ModuleEditor';
import QuillEditor from '../components/QuillEditor';
import MediaPickerModal from '../components/MediaPickerModal';


const MODULE_ICONS = {
  slider: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M7 12h.01M17 12h.01M12 16a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" /></svg>,
  'offer-sliders': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M8 10h8M8 14h5" /></svg>,
  text: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h10" /></svg>,
  html: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 18h16M8 9l-2 3 2 3M16 9l2 3-2 3" /></svg>,
  headline: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h10M4 17h16" /></svg>,
  image: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" /></svg>,
  'image-text-button': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h7M7 12h10M7 16h6" /><rect x="14" y="15" width="6" height="3" rx="1.5" /></svg>,
  button: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="7" width="18" height="10" rx="5" /><path d="M9 12h6" /></svg>,
  divider: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12h18" /><circle cx="12" cy="12" r="2" /></svg>,
  video: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M10 9l5 3-5 3V9z" /></svg>,
  products: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M4 4h6v6H4zm10 0h6v6h-6zM4 14h6v6H4zm10 0h6v6h-6z" /></svg>,
  blogs: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M4 5h16v14H4z" /><path d="M8 9h8M8 13h8M8 17h4" /></svg>,
  'split-banner': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="4" width="9" height="16" rx="1" /><rect x="13" y="4" width="9" height="16" rx="1" /></svg>,
  'promo-strip': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="8" width="20" height="8" rx="1" /><path d="M6 12h12" /></svg>,
  popup: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M7 8h10M7 12h6" /><path d="M12 18v2" /></svg>,
  categories: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M8 12h12M8 18h12M4 12v.01M4 18v.01" /></svg>,
  'feature-promo': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="3" /><path d="M7 8h10M7 12h7M7 16h4" /></svg>,
  'feature-promo-reverse': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="3" /><path d="M17 8H7M17 12h-7M17 16h-4" /></svg>,
  spacer: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 17h16" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6" /></svg>,
  brands: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 7h10v10H7z" opacity="0.5" /><circle cx="12" cy="12" r="10" /><path d="M16 8L8 16M8 8l8 8" /></svg>,
  announcement: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>,
  'offer-hero': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="12" rx="2" /><circle cx="17" cy="10" r="3" /></svg>,
  'offer-featured': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="4" rx="1" /><rect x="2" y="9" width="6" height="10" rx="1" /><rect x="9" y="9" width="6" height="10" rx="1" /><rect x="16" y="9" width="6" height="10" rx="1" /></svg>,
  'offer-catalogs': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M4 6h16v12H4z" /><path d="M8 6v12" /><path d="M16 6v12" /></svg>,
  'offer-needs': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="8" width="6" height="8" rx="4" /><rect x="9" y="8" width="6" height="8" rx="4" /><rect x="16" y="8" width="6" height="8" rx="4" /></svg>,
  'offer-footer': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M2 12h20" /><rect x="7" y="14" width="10" height="6" rx="3" /></svg>,
  'home-oferta': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M4 6h7v10H4zM13 6h7v10h-7z" /><path d="M4 3h16" /></svg>,
  oferta: <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>,
  'hr-slider': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="10" width="18" height="10" rx="2" /><path d="M7 10V6a2 2 0 012-2h6a2 2 0 012 2v4" /></svg>,
  'vertical-banner': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="6" y="2" width="12" height="20" rx="2" /><path d="M10 6h4M10 18h4" /></svg>,
  'head-tags': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>,
  'metodat-pagese': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" opacity="0.5" /><circle cx="12" cy="12" r="4" /></svg>,
  'dyqani-afer-teje': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>,
  'double-image-movement': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="2" y="4" width="14" height="16" rx="1" /><rect x="18" y="4" width="4" height="16" rx="1" /><path d="M7 10l2 2-2 2M13 14l-2-2 2-2" /></svg>,
  'bundle-offer': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="18" rx="1" /><rect x="14" y="3" width="7" height="18" rx="1" /><path d="M10 12h4" /></svg>,
  'old-to-new-offer': <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path d="M4 16l4-8 4 8" /><path d="M6.5 13h5" /><path d="M15 8l3 3 3-3" /><path d="M18 5v6" /></svg>
};

function PaletteSection({ title, defaultOpen, items, createModule, addModuleToPage }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  useEffect(() => setIsOpen(defaultOpen), [defaultOpen]);
  return (
    <div style={{ borderBottom: '1px solid #e2e8f0' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '10px 12px',
          background: '#f8fafc',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: '#334155'
        }}
      >
        <span>{title}</span>
        <span style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', fontSize: 10 }}>▼</span>
      </div>

      {isOpen && (
        <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8, background: '#fff' }}>
          {items.map(t => (
            <div key={t} onClick={() => addModuleToPage(t)}>
              <PaletteItem type={t} label={t === 'html' ? 'HTML / JS' : t.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaletteItem({ type, label }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `palette-${type}` });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} className="admin-palette-item"
      style={{
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        cursor: 'grab',
        padding: 6,
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        background: '#fff',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{
        width: 40, height: 32,
        background: '#f1f5f9',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#475569'
      }}>
        {MODULE_ICONS[type] || <span style={{ fontSize: 9, fontWeight: 700 }}>{type.substring(0, 3)}</span>}
      </div>
      <div style={{ fontSize: 11, fontWeight: 500, color: '#334155', textAlign: 'center', lineHeight: 1.1 }}>{label}</div>
    </div>
  );
}

export default function AdminPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState({ title: '', slug: '', type: 'custom', content: '', order: 0, visible: true, showHeader: false, locked: false });
  const [editId, setEditId] = useState(null);
  const [isPageFormOpen, setIsPageFormOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'editor'
  const [listTab, setListTab] = useState('all'); // 'all', 'header', 'category', 'other'
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [globalBrandLogos, setGlobalBrandLogos] = useState({});
  const [productMode, setProductMode] = useState('category'); // category | offer | manual
  const [selectedCategory, setSelectedCategory] = useState('');
  const [manualProductIds, setManualProductIds] = useState('');
  const [sliderSlides, setSliderSlides] = useState([]);
  const [modules, setModules] = useState([]); // array of page modules (drag/drop)
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [mediaPickerConfig, setMediaPickerConfig] = useState(null);

  const openMediaPicker = useCallback((onSelect) => {
    setMediaPickerConfig({ onSelect });
  }, []);
  const modulesNodeRef = useRef(null);
  const modulesCanvasRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(MouseSensor),
    useSensor(TouchSensor)
  );
  const [activeId, setActiveId] = useState(null);
  const activeType = activeId && String(activeId).startsWith('palette-') ? String(activeId).replace(/^palette-/, '') : null;
  useEffect(() => {
    if (!hasPermission('admin.pages')) {
      navigate('/login');
    }
  }, [navigate]);
  // stable id generator for modules
  const createId = () => `id_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const createModule = (t) => {
    if (t === 'slider') return { id: createId(), type: 'slider', config: [] };
    if (t === 'offer-sliders') return { id: createId(), type: 'offer-sliders', config: { title: '', items: [], titleColor: '' } };
    if (t === 'text') return { id: createId(), type: 'text', config: { text: '' } };
    if (t === 'html') return { id: createId(), type: 'html', config: { html: '' } };
    if (t === 'head-tags') return { id: createId(), type: 'head-tags', config: { code: '' } };
    if (t === 'headline') return { id: createId(), type: 'headline', config: { text: '', align: 'left', color: '', fontSize: 0 } };
    if (t === 'image') return { id: createId(), type: 'image', config: { url: '', alt: '', width: 0, height: 0 } };
    if (t === 'image-text-button') return {
      id: createId(),
      type: 'image-text-button',
      config: {
        title: 'Your Title',
        text: 'Add your content here...',
        image: '',
        imageAlt: 'Image',
        imagePosition: 'left',
        buttonText: 'Learn More',
        buttonLink: '',
        buttonAlign: 'left',
        titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: ''
      }
    };
    if (t === 'button') return { id: createId(), type: 'button', config: { text: 'Click Here', link: '', align: 'left', variant: 'primary', newTab: false } };
    if (t === 'divider') return { id: createId(), type: 'divider', config: { thickness: 1, color: '#e2e8f0', style: 'solid', width: 100, margin: 24 } };
    if (t === 'video') return { id: createId(), type: 'video', config: { url: '', title: '', aspect: '16:9', autoplay: false, muted: true, controls: true, loop: false } };
    if (t === 'products') return { id: createId(), type: 'products', config: { mode: 'category', categories: [], category: '', items: [], limit: 8, title: 'Products', filters: {}, titleColor: '' } };
    if (t === 'blogs') return { id: createId(), type: 'blogs', config: { mode: 'category', categories: [], category: '', items: [], limit: 8, title: 'Latest Posts', titleColor: '' } };
    if (t === 'split-banner') return { id: createId(), type: 'split-banner', config: { left: { image: '', alt: '', link: '' }, right: { image: '', alt: '', link: '' }, titleColor: '' } };
    if (t === 'promo-strip') return { id: createId(), type: 'promo-strip', config: { text: '', description: '', linkText: '', linkUrl: '', imageDesktop: '', imageMobile: '', image: '', titleColor: '' } };
    if (t === 'popup') return { id: createId(), type: 'popup', config: { title: '', text: '', imageDesktop: '', imageMobile: '', image: '', showBanner: true, showText: true, titleColor: '', link: '' } };
    if (t === 'vertical-banner') return { id: createId(), type: 'vertical-banner', config: { items: [{ image: '', alt: 'Banner', link: '' }], effect: 'fade', speed: 5 } };
    if (t === 'categories') return { id: createId(), type: 'categories', config: { selected: [], title: 'Shop by Category', titleColor: '' } };
    if (t === 'feature-promo') return { id: createId(), type: 'feature-promo', config: { promoTitle: 'Më e mira për shtëpinë', promoSubtitle: 'teknologjia AEG', promoText: '', promoImage: '', promoImageAlt: 'img_2', promoButtonText: '', promoButtonLink: '', titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '' } };
    if (t === 'feature-promo-reverse') return { id: createId(), type: 'feature-promo-reverse', config: { promoTitle: 'Më e mira për shtëpinë', promoSubtitle: 'teknologjia AEG', promoText: '', promoImage: '', promoImageAlt: 'img_2', promoButtonText: '', promoButtonLink: '', titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '' } };
    if (t === 'spacer') return { id: createId(), type: 'spacer', config: { height: 40 } };
    if (t === 'brands') return { id: createId(), type: 'brands', config: { title: 'Our Brands', customLogos: {}, navBgColor: '#dededeff', navHoverBgColor: '#0891b2' } };
    if (t === 'announcement') return { id: createId(), type: 'announcement', config: { items: [{ text: 'LOREM IPSUM...', linkText: '', linkUrl: '' }], speed: 5 } };
    if (t === 'contact-form') return {
      id: createId(),
      type: 'contact-form',
      config: {
        title: 'Contact Us',
        submitText: 'Send Message',
        receivingEmail: '',
        titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '',
        fields: [
          { id: createId(), name: 'name', label: 'Name', type: 'text', placeholder: 'Your name', required: true },
          { id: createId(), name: 'email', label: 'Email', type: 'email', placeholder: 'you@example.com', required: true },
          { id: createId(), name: 'message', label: 'Message', type: 'textarea', placeholder: 'How can we help?', required: true }
        ]
      }
    };

    // OFERTA MODULES
    if (t === 'offer-hero') return { id: createId(), type: 'offer-hero', config: { titlePart1: 'OFERTA', titlePart2: 'PËR', titlePart3: 'TY.', image: '', titleColor: '' } };
    if (t === 'offer-featured') return {
      id: createId(), type: 'offer-featured', config: {
        title: 'OFERTA SOT',
        titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '',
        categories: [
          { label: 'Elektroshtëpiake', link: '' },
          { label: 'Televizor', link: '' },
          { label: 'IT & Mobile', link: '' }
        ],
        cards: [
          { badge: '29% ULJE', buttonText: 'BLI TANI', image: '', link: '' },
          { badge: '25% ULJE', buttonText: 'BLI TANI', image: '', link: '' },
          { badge: '40% ULJE', buttonText: 'BLI TANI', image: '', link: '' }
        ]
      }
    };
    if (t === 'offer-catalogs') return { id: createId(), type: 'offer-catalogs', config: { title: 'KATALOGU AKTIV', sticker: 'VENDOSEN KATALOGJET', items: [{ dates: '24-31 JANAR' }], titleColor: '' } };
    if (t === 'offer-needs') return { id: createId(), type: 'offer-needs', config: { title: 'ZGJIDHJE SIPAS NEVOJËS', titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '', items: [{ label: 'Për shtëpinë', link: '' }, { label: 'Për familjen', link: '' }, { label: 'Për mua', link: '' }] } };
    if (t === 'offer-footer') return { id: createId(), type: 'offer-footer', config: { text: 'OFERTA NUK PRET. AS NEVOJA JOTE', buttonText: 'Gjej dyqanin afër meje', buttonLink: '', titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '' } };
    if (t === 'home-oferta') return { id: createId(), type: 'home-oferta', config: { title: 'Fletëpalosjet & Ofertat e fundit', leftSlides: [{ image: '', title: '', link: '' }], rightSlides: [{ image: '', title: '', link: '' }], titleColor: '' } };
    if (t === 'oferta') return {
      id: createId(), type: 'oferta', config: {
        title: 'OFERTA SOT',
        titleColor: '', buttonBgColor: '', buttonTextColor: '', buttonHoverBgColor: '', buttonHoverTextColor: '',
        tabs: [
          {
            label: 'Elektroshtëpiake',
            cards: [
              { image: '', link: '', buttonText: 'BLI TANI' },
              { image: '', link: '', buttonText: 'BLI TANI' },
              { image: '', link: '', buttonText: 'BLI TANI' }
            ]
          },
          {
            label: 'Televizor',
            cards: [
              { image: '', link: '', buttonText: 'BLI TANI' },
              { image: '', link: '', buttonText: 'BLI TANI' },
              { image: '', link: '', buttonText: 'BLI TANI' }
            ]
          },
          {
            label: 'IT & Mobile',
            cards: [
              { image: '', link: '', buttonText: 'BLI TANI' },
              { image: '', link: '', buttonText: 'BLI TANI' },
              { image: '', link: '', buttonText: 'BLI TANI' }
            ]
          }
        ]
      }
    };
    if (t === 'hr-slider') return {
      id: createId(), type: 'hr-slider', config: {
        title: 'PERSE TE BEHESH PJESE E GLOBE?',
        titleColor: '',
        slides: [
          { image: '', title: 'KULTURA KORPORATIVE', link: '' },
          { image: '', title: 'STRATEGJIA E BURIMEVE NJEREZORE', link: '' },
          { image: '', title: 'ZHVILLIMI I PUNONJESVE TANE', link: '' }
        ]
      }
    };
    if (t === 'metodat-pagese') return {
      id: createId(), type: 'metodat-pagese', config: {
        titleColor: '#002f4a',
        descColor: '#475569',
        dotColor: '#9ca3af',
        bgColor: '#f8fafc',
        items: [
          { title: 'Cash', description: 'Pagesa mund te behet cash ne momentin e dorezimit.' },
          { title: 'Transferte Bankare', description: 'Bej pagesen permes llogarise bankare.' },
          { title: 'Karta Debiti', description: 'Paguaj me karte debiti.' },
          { title: 'Karte Krediti', description: 'Paguaj me karte krediti.' },
          { title: 'Financime/Kredi', description: 'Mundesi financimi me keste.' }
        ]
      }
    };

    if (t === 'dyqani-afer-teje') return {
      id: createId(), type: 'dyqani-afer-teje', config: {
        title: 'Dyqani afër teje',
        titleColor: '#1a1a1a'
      }
    };

    if (t === 'double-image-movement') return {
      id: createId(),
      type: 'double-image-movement',
      config: {
        imageLeft: '',
        imageRight: '',
        linkLeft: '',
        linkRight: '',
        splitPercent: 80,
        movingImage: 'both', // left, right, both
        topImage: 'left',    // left, right
        direction: 'right', // left, right, top, bottom
        trigger: 'hover',   // hover, time, click
        effect: 'slide',    // slide, fade, zoom
        transitionSpeed: 0.5,
        autoInterval: 3000
      }
    };

    if (t === 'bundle-offer') return {
      id: createId(),
      type: 'bundle-offer',
      config: {
        title: 'Oferta Bundle',
        limitMode: 'all', // 'all', 'category', 'single_product'
        categories: [],
        singleProductId: '',
        titleColor: '#003b49'
      }
    };

    if (t === 'old-to-new-offer') return {
      id: createId(),
      type: 'old-to-new-offer',
      config: {
        categoryDiscounts: {
          '3031': 40, // Elektroshtepiake te medha
          '3032': 30, // Elektroshtepiake te vogla
          '3033': 25, // Telefonia
          '3034': 20, // TV & Audio
          '3035': 40, // Ftohje & Ngrohje
          '3036': 20, // Kompjutera
          '3037': 15, // Kujdesi Personal
          '3038': 40, // Pastrimi & Hekurosja
          '2812': 20, // Foto & Video
          '2813': 15, // Lojra & Hobi
          '2814': 15, // Siguria & Mbikqyrja
          '2815': 10, // Bateri & Llampa
        }
      }
    };

    return { id: createId(), type: t, config: {} };
  };
  const { isOver: isModulesOver, setNodeRef: setModulesRef } = useDroppable({ id: 'modules-container' });
  const setModulesContainerRef = useCallback((node) => {
    setModulesRef(node);
    modulesNodeRef.current = node;
  }, [setModulesRef]);

  const preserveEditorScroll = useCallback((fn) => {
    const canvas = modulesCanvasRef.current;
    const canvasTop = canvas ? canvas.scrollTop : null;
    const winY = (typeof window !== 'undefined') ? window.scrollY : null;

    if (typeof fn === 'function') fn();

    const restore = () => {
      try {
        if (canvas && canvasTop != null) canvas.scrollTop = canvasTop;
        if (typeof window !== 'undefined' && winY != null) window.scrollTo({ top: winY, left: 0, behavior: 'auto' });
      } catch (_) {
        // ignore
      }
    };

    // Restore more than once: native controls (like <input type="color">)
    // can trigger browser auto-scroll after the initial click/focus.
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
      setTimeout(restore, 50);
    });
  }, []);

  const addModuleToPage = (type) => {
    const newMod = { hidden: false, ...createModule(type) };
    setModules((m) => [...(m || []), newMod]);
  };


  const load = async () => {
    try {
      const token = getToken();
      const data = await getAllPagesAdmin(token);
      // guard against unexpected API responses (could be an error object)
      if (Array.isArray(data)) {
        setPages(data);
      } else if (data && Array.isArray(data.pages)) {
        setPages(data.pages);
      } else {
        console.warn('Unexpected pages response, expected array but got:', data);
        setPages([]);
      }
    } catch (err) { console.error(err); }
  };

  const serverBase = API_BASE_URL.replace(/\/api\/?$/i, '');
  const resolveUploadUrl = (u) => {
    if (!u) return u;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    if (u.startsWith('//')) return window.location.protocol + u;
    // treat root-relative uploads and other root assets as coming from server base
    if (u.startsWith('/')) return serverBase + u;
    return u;
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const cats = await getCategories();
        setCategories(cats || []);
      } catch (err) { console.error(err); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const svc = await import('../services/productService');
        const brandsWithImages = await svc.getBrandsWithImages();
        const brands = Array.isArray(brandsWithImages) ? brandsWithImages : [];
        const names = new Set();
        brands.forEach((b) => {
          const n = (b && b.name ? String(b.name) : '').trim();
          if (n) names.add(n);
        });
        setAvailableBrands(Array.from(names).sort((a, b) => a.localeCompare(b)));
      } catch (err) {
        console.error(err);
        setAvailableBrands([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cats = await getBlogCategories();
        setBlogCategories(cats || []);
      } catch (err) { console.error(err); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBrandLogos();
        if (data && typeof data === 'object' && !Array.isArray(data)) setGlobalBrandLogos(data);
      } catch (err) {
        console.error(err);
        setGlobalBrandLogos({});
      }
    })();
  }, []);


  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const payload = { ...form };
      if (payload.slug && typeof payload.slug === 'string') payload.slug = payload.slug.trim();

      // If modules have been used, store modules array in content
      if (modules && modules.length) {
        payload.content = modules;
      } else {
        // build structured content depending on type (backwards compat)
        if (payload.type === 'products') {
          let modConfig = { mode: 'category', categories: [], items: [], limit: 12, title: '' };
          if (productMode === 'category') {
            modConfig = { ...modConfig, mode: 'category', categories: [selectedCategory].filter(Boolean) };
          } else if (productMode === 'offer') {
            modConfig = { ...modConfig, mode: 'offer' };
          } else {
            const ids = (manualProductIds || '').split(',').map(s => Number(s.trim())).filter(Boolean);
            modConfig = { ...modConfig, mode: 'manual', items: ids };
          }
          payload.content = [{ id: 'auto_' + Date.now(), type: 'products', config: modConfig }];
        } else if (payload.type === 'slider' && sliderSlides.length) {
          payload.content = [{ id: 'auto_' + Date.now(), type: 'slider', config: sliderSlides.slice() }];
        } else if (payload.type === 'blogs') {
          payload.content = [{ id: 'auto_' + Date.now(), type: 'blogs', config: { mode: 'category', limit: 8, title: '' } }];
        } else {
          try { payload.content = JSON.parse(form.content); } catch (_) { payload.content = form.content; }
        }
      }

      // Normalize module image URLs to absolute server URLs so saved content loads in admin
      if (Array.isArray(payload.content)) {
        payload.content = payload.content.map(mod => {
          const copy = JSON.parse(JSON.stringify(mod));
          try {
            if (copy.type === 'hr-slider' && copy.config && Array.isArray(copy.config.slides)) {
              copy.config.slides = copy.config.slides.map(s => ({
                ...s,
                image: s.image ? (s.image.startsWith('http') ? s.image : serverBase + (s.image.startsWith('/') ? s.image : '/' + s.image)) : s.image
              }));
            }
            if (copy.type === 'slider' && Array.isArray(copy.config)) {
              copy.config = copy.config.map(s => ({
                ...s,
                image: s.image ? (s.image.startsWith('http') ? s.image : serverBase + (s.image.startsWith('/') ? s.image : '/' + s.image)) : s.image
              }));
            }
            if (copy.type === 'offer-sliders' && copy.config && Array.isArray(copy.config.items)) {
              copy.config.items = copy.config.items.map(s => ({
                ...s,
                image: s.image ? (s.image.startsWith('http') ? s.image : serverBase + (s.image.startsWith('/') ? s.image : '/' + s.image)) : s.image
              }));
            }
            if (copy.type === 'home-oferta' && copy.config) {
              const normal = (slides) => (Array.isArray(slides) ? slides : []).map(s => ({
                ...s,
                image: s.image ? (s.image.startsWith('http') ? s.image : serverBase + (s.image.startsWith('/') ? s.image : '/' + s.image)) : s.image
              }));
              copy.config.leftSlides = normal(copy.config.leftSlides);
              copy.config.rightSlides = normal(copy.config.rightSlides);
            }
            if (copy.type === 'oferta' && copy.config && Array.isArray(copy.config.tabs)) {
              copy.config.tabs = copy.config.tabs.map(tab => ({
                ...tab,
                cards: (tab.cards || []).map(c => ({
                  ...c,
                  image: c.image ? (c.image.startsWith('http') ? c.image : serverBase + (c.image.startsWith('/') ? c.image : '/' + c.image)) : c.image
                }))
              }));
            }
            if (copy.type === 'image' && copy.config && copy.config.url) {
              const u = copy.config.url;
              copy.config.url = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
            }
            if (copy.type === 'split-banner' && copy.config) {
              if (copy.config.left && copy.config.left.image) {
                const u = copy.config.left.image;
                copy.config.left.image = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              }
              if (copy.config.right && copy.config.right.image) {
                const u = copy.config.right.image;
                copy.config.right.image = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              }
            }
            if (copy.type === 'promo-strip' && copy.config) {
              const fields = ['imageDesktop', 'imageMobile', 'image'];
              fields.forEach((k) => {
                const u = copy.config[k];
                if (u) copy.config[k] = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              });
            }
            if (copy.type === 'popup' && copy.config) {
              const fields = ['imageDesktop', 'imageMobile', 'image'];
              fields.forEach((k) => {
                const u = copy.config[k];
                if (u) copy.config[k] = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              });
            }
            if ((copy.type === 'feature-promo' || copy.type === 'feature-promo-reverse') && copy.config && copy.config.promoImage) {
              const u = copy.config.promoImage;
              copy.config.promoImage = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
            }
            if (copy.type === 'brands' && copy.config && copy.config.customLogos && typeof copy.config.customLogos === 'object') {
              Object.keys(copy.config.customLogos).forEach((k) => {
                const u = copy.config.customLogos[k];
                if (!u) return;
                copy.config.customLogos[k] = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              });
            }
            if (copy.type === 'double-image-movement' && copy.config) {
              if (copy.config.imageLeft) {
                const u = copy.config.imageLeft;
                copy.config.imageLeft = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              }
              if (copy.config.imageRight) {
                const u = copy.config.imageRight;
                copy.config.imageRight = u.startsWith('http') ? u : serverBase + (u.startsWith('/') ? u : '/' + u);
              }
            }
            if (copy.type === 'vertical-banner' && copy.config && Array.isArray(copy.config.items)) {
              copy.config.items = copy.config.items.map(s => ({
                ...s,
                image: s.image ? (s.image.startsWith('http') ? s.image : serverBase + (s.image.startsWith('/') ? s.image : '/' + s.image)) : s.image
              }));
            }
          } catch (err) { /* ignore normalization errors */ }
          return copy;
        });
      }

      if (editId) {
        await updatePage(editId, payload, token);
        toast.success('Faqja u ruajt me sukses');
        // Do NOT reset form or editId logic here for existing page edits
        // Reload to sync state
        await load();
      } else {
        await addPage(payload, token);
        toast.success('Faqja u krijua me sukses');
        setForm({ title: '', slug: '', type: 'custom', content: '', order: 0, visible: true, showHeader: false });
        setEditId(null);
        setProductMode('category');
        setSelectedCategory('');
        setManualProductIds('');
        setSliderSlides([]);
        setModules([]);
        await load();
      }
    } catch (err) { console.error(err); toast.error('Dështoi ruajtja e faqes'); }
  };

  // helper to set nested module field (supports numeric array indices in path)
  const setModuleField = (mIndex, path, value) => {
    setModules(ms => {
      const copy = JSON.parse(JSON.stringify(ms || []));
      const parts = path.split('.');
      let cur = copy[mIndex];
      if (!cur) return ms;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (/^\d+$/.test(p)) {
          cur = cur[Number(p)];
        } else {
          cur[p] = cur[p] === undefined ? {} : cur[p];
          cur = cur[p];
        }
      }
      const last = parts[parts.length - 1];
      if (/^\d+$/.test(last)) cur[Number(last)] = value; else cur[last] = value;
      return copy;
    });
  };

  const handleFileUpload = async (mIndex, path, file) => {
    if (!file) return;
    if (!editId) { toast.error('Ju lutem ruajeni faqen para se të ngarkoni imazhe.'); return; }
    try {
      const token = getToken();
      const res = await uploadPageImage(editId, file, token);
      if (res && res.url) {
        setModuleField(mIndex, path, res.url);
        toast.success('Imazhi u ngarkua');
      } else {
        toast.error('Ngarkimi dështoi');
      }
    } catch (e) { console.error(e); toast.error('Gabim gjatë ngarkimit'); }
  };

  const handleEdit = (p) => {
    setViewMode('editor');
    setEditId(p.id);
    setIsPageFormOpen(true);
    setForm({ title: p.title || '', slug: p.slug || '', type: p.type || 'custom', content: typeof p.content === 'object' ? JSON.stringify(p.content, null, 2) : (p.content || ''), order: p.order || 0, visible: !!p.visible, showHeader: !!p.showHeader, locked: !!p.locked });
    // if page content is modules array, initialize modules editor
    if (Array.isArray(p.content)) {
      // ensure each module has a stable id and hidden flag
      const normalized = p.content.map(m => ({ hidden: false, ...(m || {}), id: (m && m.id) ? m.id : createId() }));
      setModules(normalized);
    } else {
      setModules([]);
    }
  };

  const startNewPage = () => {
    setViewMode('editor');
    setEditId(null);
    setForm({ title: '', slug: '', type: 'custom', content: '', order: 0, visible: true, showHeader: false, locked: false });
    setModules([]);
    setIsPageFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await deletePage(id, getToken());
      await load();
      toast.success('Faqja u fshi me sukses');
    } catch (err) {
      console.error(err);
      toast.error('Dështoi fshirja e faqes');
    }
  };

  // Palette draggable item (thumbnail + label). Drag from palette to insert new module.

  // Palette draggable item (thumbnail + label). Drag from palette to insert new module.


  return (
    <div>


      {viewMode === 'list' ? (() => {
        const pageSize = 15;
        const isCatBrand = (s) => s && (s.startsWith('products-category-') || s.startsWith('products-brand-') || s === 'products-global');

        // Filter by tab and search
        const filteredPages = pages.filter(p => {
          const matchesSearch = !searchQuery ? true : 
            ((p.title || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
             (p.slug || '').toLowerCase().includes(searchQuery.toLowerCase()));
             
          if (!matchesSearch) return false;

          if (listTab === 'header') return p.showHeader;
          if (listTab === 'category') return isCatBrand(p.slug);
          if (listTab === 'other') return !p.showHeader && !isCatBrand(p.slug);
          return true; // 'all'
        });

        // Sort: Locked first, then Header, then Kategori/Brand, then Other
        const sortedPages = [...filteredPages].sort((a, b) => {
          if (a.locked !== b.locked) return a.locked ? -1 : 1;
          const rankA = a.showHeader ? 1 : isCatBrand(a.slug) ? 2 : 3;
          const rankB = b.showHeader ? 1 : isCatBrand(b.slug) ? 2 : 3;
          
          if (rankA !== rankB) return rankA - rankB;
          return (a.order || 0) - (b.order || 0); // fallback to order if same group
        });

        const totalPages = Math.ceil(sortedPages.length / pageSize) || 1;
        const currentData = sortedPages.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        // Calculate counts for tabs
        const counts = {
          all: pages.length,
          header: pages.filter(p => p.showHeader).length,
          category: pages.filter(p => isCatBrand(p.slug)).length,
          other: pages.filter(p => !p.showHeader && !isCatBrand(p.slug)).length
        };

        const TabButton = ({ id, label }) => (
          <button
            onClick={() => { setListTab(id); setCurrentPage(1); }}
            style={{
              padding: '8px 16px',
              background: listTab === id ? '#eff6ff' : 'transparent',
              color: listTab === id ? '#2563eb' : '#64748b',
              border: 'none',
              borderBottom: listTab === id ? '2px solid #2563eb' : '2px solid transparent',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            {label} 
            <span style={{ 
              background: listTab === id ? '#dbeafe' : '#f1f5f9', 
              color: listTab === id ? '#1e40af' : '#64748b', 
              padding: '2px 6px', 
              borderRadius: 12, 
              fontSize: 11 
            }}>
              {counts[id]}
            </span>
          </button>
        );

        return (
          <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '0 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
              
              <div style={{ display: 'flex', gap: 4 }}>
                <TabButton id="all" label="Të Gjitha" />
                <TabButton id="header" label="Header" />
                <TabButton id="category" label="Kategori & Brande" />
                <TabButton id="other" label="Të Tjera" />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0' }}>
                <div style={{ position: 'relative' }}>
                  <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Kërko faqe..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{
                      padding: '8px 12px 8px 32px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none',
                      width: '240px',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>
                <button className="admin-btn admin-btn-primary" onClick={startNewPage} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Shto Faqe
                </button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 14 }}>
                <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  <tr>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Title / Slug</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Location</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? currentData.map((p) => {
                    const isCB = isCatBrand(p.slug);
                    return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.2s' }} onClick={() => handleEdit(p)} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {p.title}
                          {p.locked && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>/{p.slug}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {p.showHeader ? (
                          <span style={{ fontSize: 11, padding: '4px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 4, fontWeight: 500 }}>Header</span>
                        ) : isCB ? (
                          <span style={{ fontSize: 11, padding: '4px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 4, fontWeight: 500 }}>Kategori/Brand</span>
                        ) : (
                          <span style={{ fontSize: 11, color: '#64748b' }}>Të Tjera</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span className={`admin-badge ${p.visible ? 'success' : 'neutral'}`} style={{ fontSize: 11, padding: '4px 8px', background: p.visible ? '#dcfce7' : '#f1f5f9', color: p.visible ? '#166534' : '#64748b', borderRadius: 6, fontWeight: 500 }}>
                          {p.visible ? 'Visible' : 'Hidden'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 8 }}>
                          <button onClick={(e) => { e.stopPropagation(); handleEdit(p); }} style={{ border: 'none', background: 'none', color: '#3b82f6', fontSize: 13, cursor: 'pointer', fontWeight: 500, padding: '4px 8px', borderRadius: 4 }} onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>Edit</button>
                          {(!p.locked && hasPermission('admin.pages.delete')) && (
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} style={{ border: 'none', background: 'none', color: '#ef4444', fontSize: 13, cursor: 'pointer', fontWeight: 500, padding: '4px 8px', borderRadius: 4 }} onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="4" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No pages found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage(c => Math.max(1, c - 1))}>Prev</button>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>Page {currentPage} of {totalPages}</span>
                <button className="admin-btn admin-btn-secondary admin-btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))}>Next</button>
              </div>
            )}
          </div>
        );
      })() : (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ paddingBottom: 16 }}>
            <button className="admin-btn admin-btn-secondary" onClick={() => { setViewMode('list'); setEditId(null); setIsPageFormOpen(false); }}>
              &larr; Back to Pages
            </button>
          </div>
          <div className="admin-builder-layout">
            {/* Left: Modules Editor */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%', overflow: 'hidden' }}>
          {/* Module Palette */}
          <div className="admin-card" style={{ padding: 0, marginBottom: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 12, borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Available Modules
            </div>

            <div className="admin-palette-scroll" style={{ overflowY: 'auto', flex: 1 }}>
              {/* Modules Section (Default, Collapsed initially) */}
              <PaletteSection
                title="Modules"
                defaultOpen={false}
                items={[
                  'slider', 'offer-sliders', 'text', 'html', 'headline', 'image', 'image-text-button', 'button',
                  'divider', 'video', 'spacer', 'products', 'blogs', 'split-banner',
                  'promo-strip', 'popup', 'vertical-banner', 'categories', 'brands', 'announcement', 'contact-form',
                  'feature-promo', 'feature-promo-reverse', 'hr-slider', 'head-tags', 'metodat-pagese',
                  'dyqani-afer-teje', 'double-image-movement'
                ]}
                createModule={createModule}
                addModuleToPage={addModuleToPage}
              />

              {/* Oferta Section (New) */}
              <PaletteSection
                title="CMS Ofertat"
                defaultOpen={true}
                items={['offer-hero', 'offer-featured', 'offer-catalogs', 'offer-needs', 'offer-footer', 'home-oferta', 'oferta', 'bundle-offer', 'old-to-new-offer']}
                createModule={createModule}
                addModuleToPage={addModuleToPage}
              />
            </div>
          </div>

          {/* Editor Canvas */}
          <div ref={modulesCanvasRef} className="admin-card" style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', padding: 24, marginBottom: 0 }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter}
              onDragStart={(e) => {
                setActiveId(e.active?.id ?? null);
              }}
              onDragEnd={(e) => {
                const { active, over } = e;
                const overIdRaw = over?.id ?? (isModulesOver ? 'modules-container' : null);
                if (!overIdRaw) { setActiveId(null); return; }
                const overIdStr = String(overIdRaw);
                // dragging from palette into modules
                if (String(active.id).startsWith('palette-')) {
                  const type = String(active.id).replace(/^palette-/, '');
                  let insertAt = modules.length;
                  if (overIdStr.startsWith('module-')) {
                    const overId = overIdStr.replace(/^module-/, '');
                    const idx = modules.findIndex(m => m.id === overId);
                    insertAt = idx >= 0 ? idx : modules.length;
                  }
                  const newMod = { hidden: false, ...createModule(type) };
                  setModules(ms => {
                    const a = Array.from(ms || []);
                    a.splice(insertAt, 0, newMod);
                    return a;
                  });
                  setActiveId(null);
                  return;
                }
                // reorder
                if (String(active.id).startsWith('module-')) {
                  const fromId = String(active.id).replace(/^module-/, '');
                  const idxFrom = modules.findIndex(m => m.id === fromId);
                  if (overIdStr.startsWith('module-')) {
                    const toId = overIdStr.replace(/^module-/, '');
                    const idxTo = modules.findIndex(m => m.id === toId);
                    if (idxFrom >= 0 && idxTo >= 0 && idxFrom !== idxTo) setModules(ms => arrayMove(ms, idxFrom, idxTo));
                  }
                }
                setActiveId(null);
              }}
              onDragCancel={() => setActiveId(null)}
            >
              <div style={{ minHeight: '100%', position: 'relative' }} ref={setModulesContainerRef} id="modules-container">
                <SortableContext items={modules.map(m => `module-${m.id}`)} strategy={verticalListSortingStrategy}>
                  {modules.map((mod, idx) => {
                    const id = `module-${mod.id}`;
                    return (
                      <SortableModule key={id} id={id}>
                        {({ attributes, listeners, isDragging }) => (
                          <div
                            className="admin-module-item module-row"
                            style={{
                              marginBottom: 16,
                              opacity: mod.hidden ? 0.6 : 1,
                              borderStyle: mod.hidden ? 'dashed' : undefined,
                              borderColor: isDragging ? '#93c5fd' : undefined,
                              background: isDragging ? '#f8fafc' : undefined
                            }}
                          >
                            <div
                              className="admin-module-header"
                              {...attributes}
                              {...listeners}
                              style={{
                                cursor: 'grab',
                                userSelect: 'none',
                                paddingLeft: 44,
                                borderRadius: 6,
                                background: isDragging ? '#eff6ff' : 'transparent'
                              }}
                            >
                              <span style={{ color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ fontWeight: 600 }}>#{idx + 1}</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#64748b' }}>
                                  {MODULE_ICONS[mod.type] || <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>}
                                </span>
                                <span style={{ color: '#0f172a', fontWeight: 600 }}>{mod.type === 'html' ? 'HTML / JS' : mod.type.toUpperCase()}</span>
                              </span>

                              <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                                {mod.hidden ? (
                                  <span style={{ fontSize: 11, color: '#64748b', border: '1px dashed #cbd5e1', padding: '2px 6px', borderRadius: 999 }}>Hidden</span>
                                ) : null}
                                <button
                                  type="button"
                                  className="admin-btn admin-btn-primary admin-btn-sm"
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingModuleId(mod.id);
                                  }}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="admin-btn admin-btn-secondary admin-btn-sm"
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setModules(ms => ms.map((m, i) => i === idx ? { ...m, hidden: !m.hidden } : m));
                                  }}
                                >
                                  {mod.hidden ? 'Show' : 'Hide'}
                                </button>

                                <button
                                  type="button"
                                  className="admin-btn admin-btn-danger admin-btn-sm"
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onTouchStart={(e) => e.stopPropagation()}
                                  onClick={() => {
                                    setModules(ms => ms.filter((_, i) => i !== idx));
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            {/* Inline content removed, now in modal */}
                          </div>
                        )}
                      </SortableModule>
                    );
                  })}
                </SortableContext>
                <DragOverlay>
                  {activeType ? (
                    <div style={{ padding: 12, borderRadius: 8, border: '1px solid #2563eb', background: 'rgba(255,255,255,0.95)', display: 'flex', gap: 12, alignItems: 'center', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }}>
                      <div style={{ width: 40, height: 40, background: '#eff6ff', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#2563eb' }}>{activeType.substring(0, 3).toUpperCase()}</div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>{activeType === 'html' ? 'HTML / JS' : (activeType.charAt(0).toUpperCase() + activeType.slice(1))}</div>
                    </div>
                  ) : null}
                </DragOverlay>
              </div>
            </DndContext>
          </div>
        </div>

        {/* Right: New Page (dropdown) + Page Management */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, height: '100%', minWidth: 0 }}>
          {/* New Page dropdown */}
          <div className="admin-card" style={{ height: 'fit-content' }}>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              style={{ width: '100%', justifyContent: 'space-between' }}
              onClick={() => {
                setIsPageFormOpen((prev) => {
                  const next = !prev;
                  // If we are opening the form and not currently editing a page,
                  // initialize it as a fresh “New Page” form.
                  if (next && !editId) {
                    setEditId(null);
                    setForm({ title: '', slug: '', type: 'custom', content: '', order: 0, visible: true, showHeader: false });
                    setModules([]);
                  }
                  return next;
                });
              }}
            >
              <span>{editId ? 'Edit Page' : 'New Page'}</span>
              <span style={{ fontSize: 12, opacity: 0.8 }}>{isPageFormOpen ? '▲' : '▼'}</span>
            </button>

            {isPageFormOpen ? (
              <form onSubmit={handleSave} style={{ display: 'grid', gap: 16, marginTop: 16 }}>
                <div className="admin-form-group">
                  <label className="admin-label">Title</label>
                  <input className="admin-input" placeholder="Page Title" value={form.title} required onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Slug</label>
                  <input className="admin-input" placeholder="e.g. about-us" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Type</label>
                  <select className="admin-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                    <option value="custom">Custom (Modules)</option>
                    <option value="products">Products Grid</option>
                    <option value="blogs">Blog List</option>
                    <option value="slider">Full Page Slider</option>
                  </select>
                </div>
                <div className="admin-form-group">
                  <label className="admin-label">Sort Order</label>
                  <input type="number" className="admin-input" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value || 0) })} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} style={{ width: 16, height: 16 }} />
                    Visible on frontend
                  </label>
                </div>
                <div className="admin-form-group">
                  <label className="admin-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.showHeader} onChange={(e) => setForm({ ...form, showHeader: e.target.checked })} style={{ width: 16, height: 16 }} />
                    Exhibo në Header
                  </label>
                </div>
                {hasPermission('admin.pages.lock') && (
                  <div className="admin-form-group">
                    <label className="admin-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: form.locked ? '#ef4444' : 'inherit' }}>
                      <input type="checkbox" checked={form.locked} onChange={(e) => setForm({ ...form, locked: e.target.checked })} style={{ width: 16, height: 16 }} />
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: -1 }}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                      {form.locked ? 'Page is Locked (Cannot be deleted)' : 'Lock Page (Prevent deletion)'}
                    </label>
                  </div>
                )}

                <div className="admin-actions" style={{ marginTop: 8 }}>
                  <button type="submit" className="admin-btn admin-btn-primary" style={{ flex: 1 }}>Save Page</button>
                </div>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setEditId(null); setForm({ title: '', slug: '', type: 'custom', content: '', order: 0, visible: true, showHeader: false }); setModules([]); setIsPageFormOpen(false); }}>Cancel / Clear</button>
              </form>
            ) : null}
          </div>
        </div>
      </div>
      </div>
      )}
      {/* Module Edit Modal */}
      {editingModuleId && (() => {
        const idx = modules.findIndex(m => m.id === editingModuleId);
        const mod = modules[idx];
        if (!mod) return null;
        return (
          <div className="admin-modal-overlay" style={{ zIndex: 9999 }}>
            <div className="admin-modal-container large">
              <div className="admin-modal-header">
                <h2 className="admin-modal-title">Konfigurimi i Modulit: {mod.type === 'html' ? 'HTML / JAVASCRIPT' : mod.type.toUpperCase()}</h2>
                <button type="button" className="admin-modal-close" onClick={() => setEditingModuleId(null)}>&times;</button>
              </div>
              <div className="admin-modal-body" style={{ padding: 32 }}>
                <ModuleEditor
                  mod={mod}
                  idx={idx}
                  setModules={setModules}
                  categories={categories}
                  blogCategories={blogCategories}
                  availableBrands={availableBrands}
                  globalBrandLogos={globalBrandLogos}
                  setGlobalBrandLogos={setGlobalBrandLogos}
                  handleFileUpload={handleFileUpload}
                  preserveEditorScroll={preserveEditorScroll}
                  resolveUploadUrl={resolveUploadUrl}
                  TextEditor={TextEditor}
                  LocalInput={LocalInput}
                  LocalNumberInput={LocalNumberInput}
                  LocalSelect={LocalSelect}
                  LocalSelectMultiple={LocalSelectMultiple}
                  SlideEditor={SlideEditor}
                  getToken={getToken}
                  deleteBrandLogo={deleteBrandLogo}
                  setBrandLogoUrl={setBrandLogoUrl}
                  uploadBrandLogo={uploadBrandLogo}
                  toast={toast}
                  onPickMedia={openMediaPicker}
                />
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setEditingModuleId(null)}>Mbyll</button>
                <button type="button" className="admin-btn admin-btn-primary" onClick={() => setEditingModuleId(null)}>Ruaj</button>
              </div>
            </div>
          </div>
        );
      })()}

      <MediaPickerModal
        isOpen={!!mediaPickerConfig}
        onClose={() => setMediaPickerConfig(null)}
        onSelect={(url) => {
          if (mediaPickerConfig && mediaPickerConfig.onSelect) {
            mediaPickerConfig.onSelect(url);
          }
          setMediaPickerConfig(null);
        }}
      />
    </div >
  );
}

// Helper Components moved outside to be exportable/sharable
export function SortableModule({ id, hideTitle, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    boxShadow: isDragging ? '0 10px 20px rgba(0,0,0,0.1)' : undefined,
    position: 'relative',
    zIndex: isDragging ? 20 : 'auto',
    opacity: isDragging ? 0.92 : 1
  };
  if (typeof children === 'function') {
    return <div ref={setNodeRef} style={style}>{children({ attributes, listeners, isDragging })}</div>;
  }
  return <div ref={setNodeRef} style={style}>{children}</div>;
}

export function TextEditor({ value, onCommit }) {
  const [local, setLocal] = React.useState(value || '');
  React.useEffect(() => { setLocal(value || ''); }, [value]);
  return (
    <div className="admin-form-group">
      <label className="admin-label">Text</label>
      <QuillEditor
        value={local}
        onChange={(val) => {
          setLocal(val);
          onCommit(val);
        }}
        style={{ background: '#fff' }}
      />
    </div>
  );
}

export function LocalInput({ value, onCommit, placeholder, style, type = 'text', label }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  return (
    <div className="admin-form-group" style={style}>
      {label && <label className="admin-label">{label}</label>}
      <input type={type} className="admin-input" placeholder={placeholder} value={local} onChange={(e) => setLocal(e.target.value)}
        onBlur={() => onCommit(local)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onCommit(local); }} onPointerDown={(e) => e.stopPropagation()} />
    </div>
  );
}

export function LocalNumberInput({ value, onCommit, placeholder, style, label }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  return (
    <div className="admin-form-group" style={style}>
      {label && <label className="admin-label">{label}</label>}
      <input type="number" className="admin-input" placeholder={placeholder} value={local} onChange={(e) => setLocal(e.target.value)}
        onBlur={() => onCommit(Number(local || 0))} onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) onCommit(Number(local || 0)); }} onPointerDown={(e) => e.stopPropagation()} />
    </div>
  );
}

export function LocalSelect({ value, onCommit, options, style, label }) {
  const [local, setLocal] = useState(value ?? '');
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  return (
    <div className="admin-form-group" style={style}>
      {label && <label className="admin-label">{label}</label>}
      <select className="admin-select" value={local} onChange={(e) => { setLocal(e.target.value); onCommit && onCommit(e.target.value); }} onPointerDown={(e) => e.stopPropagation()}>
        {(options || []).map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}

export function LocalSelectMultiple({ value, options, onCommit, style, label }) {
  const [local, setLocal] = useState(Array.isArray(value) ? value.slice() : []);
  useEffect(() => { setLocal(Array.isArray(value) ? value.slice() : []); }, [value]);
  return (
    <div className="admin-form-group" style={style}>
      {label && <label className="admin-label">{label}</label>}
      <select multiple className="admin-select" value={local} onChange={(e) => setLocal(Array.from(e.target.selectedOptions || []).map(o => o.value))}
        onBlur={() => onCommit(local)} onPointerDown={(e) => e.stopPropagation()} style={{ minHeight: 120 }}>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}

export function SlideEditor({ slide, onCommit, onRemove, onUpload, removeLabel, resolveUploadUrl }) {
  const [local, setLocal] = useState(slide || { image: '', title: '', link: '' });
  useEffect(() => { setLocal(slide || { image: '', title: '', link: '' }); }, [slide]);
  const commitField = (p) => { const n = { ...local, ...p }; setLocal(n); onCommit && onCommit(n); };
  return (
    <div className="admin-card" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'grid', gap: 12 }}>
        <LocalInput label="Image URL" value={local.image} onCommit={(v) => commitField({ image: v })} />
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label className="admin-btn admin-btn-secondary admin-btn-sm"> Upload <input type="file" hidden accept="image/*" onChange={(e) => { if (e.target.files && e.target.files[0]) onUpload && onUpload(e.target.files[0]); }} /></label>
          {local.image ? <img src={resolveUploadUrl(local.image)} alt="preview" style={{ height: 48, borderRadius: 4 }} /> : null}
        </div>
        <LocalInput label="Title" value={local.title} onCommit={(v) => commitField({ title: v })} />
        <LocalInput label="Link" value={local.link} onCommit={(v) => commitField({ link: v })} />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="admin-btn admin-btn-danger admin-btn-sm" onClick={onRemove}>{removeLabel || 'Remove Slide'}</button>
        </div>
      </div>
    </div>
  );
}
